/**
 * Icon Picker
 *
 * Popover component for selecting category icons from a curated set of
 * finance-related icons using the shared Icon system.
 */

import { Icon, IconKey, getIconComponent } from '@/shared/components';
import { Button } from '@/shared/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { cn } from '@/shared/utils';
import { useState } from 'react';

// Curated icon set for finance categories
export const CATEGORY_ICONS: { key: IconKey; tooltip: string }[] = [
  // Income
  { key: IconKey.Briefcase, tooltip: 'Work/Salary' },
  { key: IconKey.DollarSign, tooltip: 'Money' },
  { key: IconKey.TrendingUp, tooltip: 'Investments' },
  { key: IconKey.Landmark, tooltip: 'Bank' },
  { key: IconKey.Wallet, tooltip: 'Wallet' },
  // Food
  { key: IconKey.ShoppingCart, tooltip: 'Groceries' },
  { key: IconKey.Coffee, tooltip: 'Coffee/Cafe' },
  { key: IconKey.Utensils, tooltip: 'Dining' },
  { key: IconKey.Apple, tooltip: 'Food' },
  // Home
  { key: IconKey.Home, tooltip: 'Home/Housing' },
  { key: IconKey.Lightbulb, tooltip: 'Utilities' },
  { key: IconKey.Droplet, tooltip: 'Water' },
  { key: IconKey.Wifi, tooltip: 'Internet' },
  // Transport
  { key: IconKey.Car, tooltip: 'Car/Auto' },
  { key: IconKey.Fuel, tooltip: 'Gas/Fuel' },
  { key: IconKey.Plane, tooltip: 'Travel/Flight' },
  { key: IconKey.Train, tooltip: 'Transit' },
  // Entertainment
  { key: IconKey.Film, tooltip: 'Movies' },
  { key: IconKey.Music, tooltip: 'Music' },
  { key: IconKey.Gamepad2, tooltip: 'Gaming' },
  { key: IconKey.Tv, tooltip: 'TV/Streaming' },
  // Health
  { key: IconKey.Heart, tooltip: 'Health' },
  { key: IconKey.Pill, tooltip: 'Pharmacy' },
  { key: IconKey.Activity, tooltip: 'Fitness' },
  // Shopping
  { key: IconKey.ShoppingBag, tooltip: 'Shopping' },
  { key: IconKey.Gift, tooltip: 'Gifts' },
  { key: IconKey.Shirt, tooltip: 'Clothing' },
  // Other
  { key: IconKey.Wrench, tooltip: 'Repairs' },
  { key: IconKey.GraduationCap, tooltip: 'Education' },
  { key: IconKey.Baby, tooltip: 'Kids' },
  { key: IconKey.Dog, tooltip: 'Pets' },
  { key: IconKey.CreditCard, tooltip: 'Payments' },
  { key: IconKey.Tag, tooltip: 'General' }
];

/**
 * Get the IconKey for a category icon name string (from database).
 * Since IconKey values match Lucide component names, this is a direct cast.
 */
export function getCategoryIconKey(name: string | null | undefined): IconKey {
  if (!name) return IconKey.Tag;
  // Check if the name is a valid IconKey
  const found = CATEGORY_ICONS.find((i) => i.key === name);
  if (found) return found.key;
  // Try casting — the enum value equals the Lucide name
  if (getIconComponent(name as IconKey)) return name as IconKey;
  return IconKey.Tag;
}

/** @deprecated Use getCategoryIconKey instead */
export function getCategoryIcon(name: string | null | undefined): IconKey {
  return getCategoryIconKey(name);
}

interface IconPickerProps {
  value: string | null | undefined;
  onChange: (value: string) => void;
}

interface InlineIconPickerProps {
  value: string | null | undefined;
  onChange: (value: string) => void;
}

export function InlineIconPicker({ value, onChange }: InlineIconPickerProps) {
  return (
    <div className='grid grid-cols-6 gap-1 max-h-[200px] overflow-y-auto rounded-lg bg-card p-2'>
      {CATEGORY_ICONS.map(({ key, tooltip }) => (
        <button
          key={key}
          type='button'
          title={tooltip}
          className={cn('p-2 rounded-md transition-colors hover:bg-accent/20', value === key && 'bg-accent/15 ring-2 ring-accent')}
          onClick={() => onChange(key)}
        >
          <Icon icon={key} size={16} className='mx-auto' />
        </button>
      ))}
    </div>
  );
}

export function IconPicker({ value, onChange }: IconPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedIconKey = getCategoryIconKey(value);

  return (
    <>
      <Button type='button' variant='outline' className='w-full justify-start gap-2' onClick={() => setIsOpen(true)}>
        <Icon icon={selectedIconKey} size={16} />
        <span className='text-text/70'>{value || 'Select icon'}</span>
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className='sm:max-w-[400px]'>
          <DialogHeader>
            <DialogTitle>Select Icon</DialogTitle>
          </DialogHeader>
          <div className='grid grid-cols-6 gap-2 py-4'>
            {CATEGORY_ICONS.map(({ key, tooltip }) => (
              <button
                key={key}
                type='button'
                title={tooltip}
                className={cn('p-3 rounded-md transition-colors hover:bg-accent/20', value === key && 'bg-accent/30 ring-2 ring-accent')}
                onClick={() => {
                  onChange(key);
                  setIsOpen(false);
                }}
              >
                <Icon icon={key} size={20} className='mx-auto' />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
